/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import {
  ICmnInspectionProps,
  IInspectionState,
  IPostInspectionResultProps,
  IInspectionSecondClassificationProps,
} from 'interface/inspection/InspectionInfo';
import { makeHeader } from 'libs/mindUtils';
import { RootState } from 'store/rootReducer';
import { IInspectionClickLog } from 'utils/inspectionLogger';

const initialState: IInspectionState = {
  progressInfo: 0,
  detailProgressInfo: 0,
  objectKeyIndex: 0,
  surveyAdd: [],
  inspectionDetail: {
    id: '',
    inspectionName: '',
    inspectionDesc: '',
    inspectionFilename: '',
    grid: '',
    sort: 0,
    inspectionIpsatives: [],
    saveFileName: '',
    regId: '',
    fileUrl: '',
  },
  classification: '1',
  isClassificationGuideStart1: false,
  isClassificationGuideStart2_1: false,
  isClassificationGuideStart2_2: false,
  isClassificationGuideStart2_3: false,
  isClassificationGuideStart2_4: false,
  isClassificationGuideStart3: false,
  isClassificationGuideStartFinal: false,
  isClassificationGuideStartAdditional: false,
  isClassificationGuideStartComplete: false,
  inspectionFirstClassification: {
    NNTemp: [],
    NTemp: [],
    ATemp: [],
    AATemp: [],
  },
  inspectionSecondClassification: {
    // NN 그룹
    NNTempNN: [],
    NNTempN: [],
    NNTempA: [],
    NNTempAA: [],
    // N 그룹
    NTempNN: [],
    NTempN: [],
    NTempA: [],
    NTempAA: [],
    // A 그룹
    ATempNN: [],
    ATempN: [],
    ATempA: [],
    ATempAA: [],
    // AA 그룹
    AATempNN: [],
    AATempN: [],
    AATempA: [],
    AATempAA: [],
  },
  inspectionThirdClassification: {},
  inspectionEndClassification: {},
  inspectionFinalBase: [],
};

const instance = axios.create({
  validateStatus(status) {
    return status >= 200 && status <= 500;
  },
});

export const postInspectionResult = createAsyncThunk<AxiosResponse, IPostInspectionResultProps, { state: RootState }>(
  'inspection/postInspectionResult',
  ({ projectId, partCode, inspectionId, ipsativeIdResultList, surveyAddResultList }, { getState }) => {
    const url = process.env.REACT_APP_URL;
    const loginInfo = getState().login.info;
    const header = makeHeader(loginInfo?.accessToken);
    const body: Omit<IPostInspectionResultProps, 'projectId'> = {
      partCode,
      inspectionId,
      ipsativeIdResultList,
      surveyAddResultList,
    };
    return instance.post(`${url}/api/result/inspection/${projectId}/save`, body, header);
  },
);

// 클릭 이벤트 로그 저장 API
export const postInspectionClickLogs = createAsyncThunk<
  AxiosResponse,
  { projectId: string; logs: IInspectionClickLog[] },
  { state: RootState }
>('inspection/postInspectionClickLogs', ({ projectId, logs }, { getState }) => {
  const url = process.env.REACT_APP_URL;
  const loginInfo = getState().login.info;
  const header = makeHeader(loginInfo?.accessToken);
  
  // 브라우저 정보를 평탄화하여 전송
  const flattenedLogs = logs.map(log => {
    const browserInfo = log.browserInfo || {};
    const flattened = {
      ...log,
      userAgent: browserInfo.userAgent || null,
      browserName: browserInfo.browserName || null,
      browserVersion: browserInfo.browserVersion || null,
      osName: browserInfo.osName || null,
      osVersion: browserInfo.osVersion || null,
      deviceType: browserInfo.deviceType || null,
      screenWidth: browserInfo.screenWidth || null,
      screenHeight: browserInfo.screenHeight || null,
      language: browserInfo.language || null,
      timezone: browserInfo.timezone || null,
    };
    
    // browserInfo 필드 제거 (평탄화되었으므로)
    delete (flattened as any).browserInfo;
    
    // 개발 환경에서 전송 데이터 확인
    if (process.env.NODE_ENV === 'development') {
      console.log('[postInspectionClickLogs] 전송할 로그:', flattened);
    }
    
    return flattened;
  });
  
  const body = { logs: flattenedLogs };
  return instance.post(`${url}/api/inspection/${projectId}/click-logs`, body, header);
});

const inspectionSlice = createSlice({
  name: 'inspection',
  initialState,
  reducers: {
    changeProgressInfo: (state, action) => {
      const { progressInfo } = action.payload;
      state.progressInfo = progressInfo;
    },
    changeDetailProgressInfo: (state, action) => {
      const { detailProgressInfo } = action.payload;
      state.detailProgressInfo = detailProgressInfo;
    },
    changeObjectKeyIndex: (state, action) => {
      const { objectKeyIndex } = action.payload;
      state.objectKeyIndex = objectKeyIndex;
    },
    changeInspectionDetail: (state, action) => {
      const { inspectionDetail } = action.payload;
      state.inspectionDetail = inspectionDetail;
    },
    changeClassification: (state, action) => {
      const { classification } = action.payload;
      state.classification = classification;
    },
    changeIsClassificationGuideStart1: (state, action) => {
      const { isClassificationGuideStart1 } = action.payload;
      state.isClassificationGuideStart1 = isClassificationGuideStart1;
    },
    changeIsClassificationGuideStart2_1: (state, action) => {
      const { isClassificationGuideStart2_1 } = action.payload;
      state.isClassificationGuideStart2_1 = isClassificationGuideStart2_1;
    },
    changeIsClassificationGuideStart2_2: (state, action) => {
      const { isClassificationGuideStart2_2 } = action.payload;
      state.isClassificationGuideStart2_2 = isClassificationGuideStart2_2;
    },
    changeIsClassificationGuideStart2_3: (state, action) => {
      const { isClassificationGuideStart2_3 } = action.payload;
      state.isClassificationGuideStart2_3 = isClassificationGuideStart2_3;
    },
    changeIsClassificationGuideStart2_4: (state, action) => {
      const { isClassificationGuideStart2_4 } = action.payload;
      state.isClassificationGuideStart2_4 = isClassificationGuideStart2_4;
    },
    changeIsClassificationGuideStart3: (state, action) => {
      const { isClassificationGuideStart3 } = action.payload;
      state.isClassificationGuideStart3 = isClassificationGuideStart3;
    },
    changeIsClassificationGuideStartFinal: (state, action) => {
      const { isClassificationGuideStartFinal } = action.payload;
      state.isClassificationGuideStartFinal = isClassificationGuideStartFinal;
    },
    changeIsClassificationGuideStartAdditional: (state, action) => {
      const { isClassificationGuideStartAdditional } = action.payload;
      state.isClassificationGuideStartAdditional = isClassificationGuideStartAdditional;
    },
    changeIsClassificationGuideStartComplete: (state, action) => {
      const { isClassificationGuideStartComplete } = action.payload;
      state.isClassificationGuideStartComplete = isClassificationGuideStartComplete;
    },
    changeResultInspection: (state, action) => {
      // 1단계 분류 결과는 inspectionFirstClassification에 저장하지 않음 (기존 로직 유지)
      // 이 함수는 호환성을 위해 유지하되, 실제로는 사용하지 않을 수 있음
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _ = action.payload;
    },
    changeInspectionFirstClassification: (state, action) => {
      const { inspectionFirstClassification } = action.payload;
      state.inspectionFirstClassification = inspectionFirstClassification;
    },
    updateInspectionFirstClassificationItem: (state, action) => {
      const { parent, item, oldParent } = action.payload;
      const newItem: ICmnInspectionProps = item;

      // 기존 카테고리에서 제거 (카테고리 변경 시)
      if (oldParent && oldParent !== parent) {
        const removeFromArray = (infoArray: ICmnInspectionProps[]) => {
          return infoArray.filter(i => i.id !== item.id);
        };

        if (oldParent === 'NN') {
          state.inspectionFirstClassification.NNTemp = removeFromArray(state.inspectionFirstClassification.NNTemp);
        } else if (oldParent === 'N') {
          state.inspectionFirstClassification.NTemp = removeFromArray(state.inspectionFirstClassification.NTemp);
        } else if (oldParent === 'A') {
          state.inspectionFirstClassification.ATemp = removeFromArray(state.inspectionFirstClassification.ATemp);
        } else if (oldParent === 'AA') {
          state.inspectionFirstClassification.AATemp = removeFromArray(state.inspectionFirstClassification.AATemp);
        }
      }

      // 새 카테고리에 추가/업데이트
      const updateInfo = (infoArray: ICmnInspectionProps[]) => {
        const index = infoArray.findIndex(i => i.id === item.id);
        if (index !== -1) {
          const copyArray = [...infoArray];
          copyArray[index] = newItem;
          return copyArray;
        }
        return [...infoArray, newItem];
      };

      if (parent === 'NN') {
        state.inspectionFirstClassification.NNTemp = updateInfo(state.inspectionFirstClassification.NNTemp);
      } else if (parent === 'N') {
        state.inspectionFirstClassification.NTemp = updateInfo(state.inspectionFirstClassification.NTemp);
      } else if (parent === 'A') {
        state.inspectionFirstClassification.ATemp = updateInfo(state.inspectionFirstClassification.ATemp);
      } else if (parent === 'AA') {
        state.inspectionFirstClassification.AATemp = updateInfo(state.inspectionFirstClassification.AATemp);
      }
    },
    updateInspectionSecondClassificationItem: (state, action) => {
      const { type, item, oldType } = action.payload;
      const newItem: ICmnInspectionProps = item;

      // 기존 타입에서 제거 (타입 변경 시)
      if (oldType && oldType !== type && state.inspectionSecondClassification[oldType as keyof IInspectionSecondClassificationProps]) {
        const oldArray = state.inspectionSecondClassification[oldType as keyof IInspectionSecondClassificationProps] as ICmnInspectionProps[];
        state.inspectionSecondClassification[oldType as keyof IInspectionSecondClassificationProps] = oldArray.filter(
          i => i.id !== item.id,
        ) as any;
      }

      // 새 타입에 추가/업데이트
      const updateInfo = (infoArray: ICmnInspectionProps[]) => {
        const index = infoArray.findIndex(i => i.id === item.id);
        if (index !== -1) {
          const copyArray = [...infoArray];
          copyArray[index] = newItem;
          return copyArray;
        }
        return [...infoArray, newItem];
      };

      if (state.inspectionSecondClassification[type as keyof IInspectionSecondClassificationProps]) {
        const currentArray = state.inspectionSecondClassification[type as keyof IInspectionSecondClassificationProps] as ICmnInspectionProps[];
        state.inspectionSecondClassification[type as keyof IInspectionSecondClassificationProps] = updateInfo(currentArray) as any;
      }
    },
    initInspectionThirdClassification: (state, action) => {
      const { groups } = action.payload;
      // 3차 분류 초기화: 20% 넘는 그룹들에 대해 4개의 하위 그룹 생성 (예: NNTempNN1, NNTempNN2, NNTempNN3, NNTempNN4)
      state.inspectionThirdClassification = {};
      groups.forEach((group: string) => {
        state.inspectionThirdClassification[`${group}1`] = [];
        state.inspectionThirdClassification[`${group}2`] = [];
        state.inspectionThirdClassification[`${group}3`] = [];
        state.inspectionThirdClassification[`${group}4`] = [];
      });
    },
    updateInspectionThirdClassificationItem: (state, action) => {
      const { type, item, oldType } = action.payload;
      const newItem: ICmnInspectionProps = item;

      // 기존 타입에서 제거 (타입 변경 시)
      if (oldType && oldType !== type && state.inspectionThirdClassification[oldType]) {
        const oldArray = state.inspectionThirdClassification[oldType];
        state.inspectionThirdClassification[oldType] = oldArray.filter(i => i.id !== item.id);
      }

      // 새 타입에 추가/업데이트
      const updateInfo = (infoArray: ICmnInspectionProps[]) => {
        const index = infoArray.findIndex(i => i.id === item.id);
        if (index !== -1) {
          const copyArray = [...infoArray];
          copyArray[index] = newItem;
          return copyArray;
        }
        return [...infoArray, newItem];
      };

      if (state.inspectionThirdClassification[type]) {
        const currentArray = state.inspectionThirdClassification[type];
        state.inspectionThirdClassification[type] = updateInfo(currentArray);
      } else {
        // 타입이 없으면 생성
        state.inspectionThirdClassification[type] = [newItem];
      }
    },
    initInspectionEndClassification: (state, _action) => {
      // inspectionSecondClassification의 16개 그룹을 순차적으로 복사
      // 단, 3차 분류를 한 그룹은 inspectionThirdClassification에서 4개 하위 그룹으로 복사
      const secondClassificationKeys: (keyof typeof state.inspectionSecondClassification)[] = [
        'NNTempNN',
        'NNTempN',
        'NNTempA',
        'NNTempAA',
        'NTempNN',
        'NTempN',
        'NTempA',
        'NTempAA',
        'ATempNN',
        'ATempN',
        'ATempA',
        'ATempAA',
        'AATempNN',
        'AATempN',
        'AATempA',
        'AATempAA',
      ];

      state.inspectionEndClassification = {};
      let order = 0; // 순서 카운터

      secondClassificationKeys.forEach(key => {
        // 3차 분류를 했는지 확인 (inspectionThirdClassification에 해당 그룹의 하위 그룹이 있는지)
        const thirdGroup1 = state.inspectionThirdClassification[`${key}1`];
        const thirdGroup2 = state.inspectionThirdClassification[`${key}2`];
        const thirdGroup3 = state.inspectionThirdClassification[`${key}3`];
        const thirdGroup4 = state.inspectionThirdClassification[`${key}4`];
        
        const hasThirdClassification = 
          (thirdGroup1 && thirdGroup1.length > 0) ||
          (thirdGroup2 && thirdGroup2.length > 0) ||
          (thirdGroup3 && thirdGroup3.length > 0) ||
          (thirdGroup4 && thirdGroup4.length > 0);

        if (hasThirdClassification) {
          // 3차 분류를 한 경우: inspectionThirdClassification에서 4개 하위 그룹 복사 (빈 배열 제외)
          // 순서: 1, 2, 3, 4 순서대로 추가
          if (thirdGroup1 && thirdGroup1.length > 0) {
            state.inspectionEndClassification[`${key}1`] = {
              order: order++,
              items: [...thirdGroup1],
            };
          }
          if (thirdGroup2 && thirdGroup2.length > 0) {
            state.inspectionEndClassification[`${key}2`] = {
              order: order++,
              items: [...thirdGroup2],
            };
          }
          if (thirdGroup3 && thirdGroup3.length > 0) {
            state.inspectionEndClassification[`${key}3`] = {
              order: order++,
              items: [...thirdGroup3],
            };
          }
          if (thirdGroup4 && thirdGroup4.length > 0) {
            state.inspectionEndClassification[`${key}4`] = {
              order: order++,
              items: [...thirdGroup4],
            };
          }
        } else {
          // 3차 분류를 하지 않은 경우: inspectionSecondClassification에서 복사 (빈 배열 제외)
          const secondGroup = state.inspectionSecondClassification[key];
          if (secondGroup && secondGroup.length > 0) {
            state.inspectionEndClassification[key] = {
              order: order++,
              items: [...secondGroup],
            };
          }
        }
      });
    },
    initInspectionFinalBase: (state, action) => {
      const { grid } = action.payload;
      
      // grid를 사용해서 방 구조만 생성 (값은 넣지 않음)
      // value가 0인 항목은 배열에 포함하지 않음
      if (grid && typeof grid === 'string' && grid.trim() !== '') {
        try {
          const gridArray = grid.split(',').map(Number);
          
          if (!gridArray.some(Number.isNaN)) {
            // value > 0인 항목만 배열에 추가
            state.inspectionFinalBase = gridArray
              .map((value, index) => {
                const key = (index - 6).toString();
                return {
                  key,
                  value: value || 0,
                  array: [],
                };
              })
              .filter((item) => item.value > 0); // value가 0보다 큰 항목만 유지
          }
        } catch (error) {
          console.error('Error processing grid:', error);
        }
      }
      
      // inspectionEndClassification의 항목들을 채우는 로직은 제거됨
      // 사용자가 하나씩 알려줄 예정
    },
    autoFillInspectionFinalBase: (state) => {
      // 이미 inspectionFinalBase의 모든 방에 들어간 항목들의 ID 수집
      const usedItemIds = new Set<string>();
      state.inspectionFinalBase.forEach((room) => {
        if (room.array) {
          room.array.forEach((item: ICmnInspectionProps) => {
            usedItemIds.add(item.id);
          });
        }
      });

      // inspectionFinalBase의 마지막 방부터 역순으로 자동 채우기
      for (let baseIndex = state.inspectionFinalBase.length - 1; baseIndex >= 0; baseIndex--) {
        const baseItem = state.inspectionFinalBase[baseIndex];
        if (!baseItem || baseItem.value === 0) continue;
        
        const requiredCount = baseItem.value;
        const alreadyFilled = baseItem.array?.length || 0;
        const needToFill = requiredCount - alreadyFilled;
        
        if (needToFill <= 0) continue; // 이미 채워진 방은 건너뛰기
        
        // inspectionEndClassification의 마지막 키 찾기 (순서 정보 사용)
        // order가 큰 순서대로 정렬 (역순)
        const endClassificationKeys = Object.keys(state.inspectionEndClassification).sort((a, b) => {
          const aOrder = state.inspectionEndClassification[a]?.order ?? -1;
          const bOrder = state.inspectionEndClassification[b]?.order ?? -1;
          return bOrder - aOrder; // 큰 순서가 앞으로 (역순)
        });
        
        if (endClassificationKeys.length === 0) break; // 더 이상 채울 항목이 없음
        
        // 정렬 후 첫 번째 키 선택 (가장 큰 order)
        const lastKey = endClassificationKeys[0];
        const lastGroup = state.inspectionEndClassification[lastKey];
        if (!lastGroup) {
          delete state.inspectionEndClassification[lastKey];
          baseIndex++; // 같은 방 다시 처리
          continue;
        }
        
        let lastItems = lastGroup.items || [];
        
        // 이미 사용된 항목 제외
        lastItems = lastItems.filter((item: ICmnInspectionProps) => !usedItemIds.has(item.id));
        
        if (lastItems.length === 0) {
          // 사용 가능한 항목이 없으면 키 제거
          delete state.inspectionEndClassification[lastKey];
          baseIndex++; // 같은 방 다시 처리
          continue;
        }
        
        if (lastItems.length <= needToFill) {
          // 항목 개수가 필요한 개수 이하이면 모두 넣기
          if (!baseItem.array) baseItem.array = [];
          baseItem.array.push(...lastItems);
          // 사용된 항목 ID 업데이트
          lastItems.forEach((item: ICmnInspectionProps) => {
            usedItemIds.add(item.id);
          });
          // inspectionEndClassification에서 제거
          delete state.inspectionEndClassification[lastKey];
          baseIndex++; // 같은 방 다시 처리 (아직 더 필요할 수 있음)
        } else {
          // 항목 개수가 필요한 개수보다 많으면 자동으로 넣지 않음
          // 화면에서 선택하게 해야 하므로 여기서는 중단
          break;
        }
      }
    },
    removeItemsFromInspectionEndClassification: (state, action) => {
      const { key, items } = action.payload;
      const currentGroup = state.inspectionEndClassification[key];
      if (!currentGroup) return;
      
      const currentItems = currentGroup.items || [];
      const itemIdsToRemove = new Set(items.map((item: ICmnInspectionProps) => item.id));
      const remainingItems = currentItems.filter((item: ICmnInspectionProps) => !itemIdsToRemove.has(item.id));
      
      if (remainingItems.length > 0) {
        state.inspectionEndClassification[key] = {
          order: currentGroup.order,
          items: remainingItems,
        };
      } else {
        // 빈 배열이면 키 제거
        delete state.inspectionEndClassification[key];
      }
    },
    changeInspectionFinalBase: (state, action) => {
      const { key, value, array } = action.payload;
      const index = state.inspectionFinalBase.findIndex(item => item.key === key);
      if (index !== -1) {
        state.inspectionFinalBase[index] = { key, value, array };
      }
    },
    changeSurveyAdd: (state, action) => {
      const { surveyAdd } = action.payload;
      state.surveyAdd = surveyAdd;
    },
    reset: () => initialState,
  },
});

export const {
  changeProgressInfo,
  changeDetailProgressInfo,
  changeObjectKeyIndex,
  changeInspectionDetail,
  changeClassification,
  changeIsClassificationGuideStart1,
  changeIsClassificationGuideStart2_1,
  changeIsClassificationGuideStart2_2,
  changeIsClassificationGuideStart2_3,
  changeIsClassificationGuideStart2_4,
  changeIsClassificationGuideStart3,
  changeIsClassificationGuideStartFinal,
  changeIsClassificationGuideStartAdditional,
  changeIsClassificationGuideStartComplete,
  changeResultInspection,
  changeInspectionFirstClassification,
  updateInspectionFirstClassificationItem,
  updateInspectionSecondClassificationItem,
  initInspectionThirdClassification,
  updateInspectionThirdClassificationItem,
  initInspectionEndClassification,
  initInspectionFinalBase,
  autoFillInspectionFinalBase,
  removeItemsFromInspectionEndClassification,
  changeInspectionFinalBase,
  changeSurveyAdd,
  reset,
} = inspectionSlice.actions;
export default inspectionSlice.reducer;
export const inspectionSelector = (state: RootState): IInspectionState => state.inspection;
